import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Loader2, MapPin, X } from 'lucide-react';

// Ensure the default Leaflet marker assets are loaded (some bundlers strip them)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

function MapController({ lat, lon }) {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lon != null) {
      map.flyTo([lat, lon], Math.max(map.getZoom(), 15), { animate: true, duration: 0.8 });
    }
  }, [lat, lon, map]);
  return null;
}

/**
 * AddressPicker · autocomplete en vivo + mapa Leaflet con pin arrastrable.
 * Props:
 *   - value: {place_id, name, address, lat, lon, type_detected} | null
 *   - onChange: (next) => void
 *   - radiusKm: number (para dibujar el círculo)
 */
export default function AddressPicker({ value, onChange, radiusKm = 3 }) {
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const boxRef = useRef(null);

  // Debounced autocomplete
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (!q || q.length < 3 || value) {
      setCandidates([]); setLoading(false); return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get('/places/resolve', { params: { q } });
        setCandidates((r.data?.candidates || []).slice(0, 8));
        setOpen(true);
      } catch { setCandidates([]); }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [query, value]);

  // Click outside → close dropdown
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (c) => {
    onChange({
      place_id: c.place_id, name: c.name, address: c.address,
      lat: c.lat, lon: c.lon, type_detected: c.type_detected,
    });
    setQuery(''); setCandidates([]); setOpen(false);
  };

  const clearSelection = () => { onChange(null); setQuery(''); setCandidates([]); };

  const markerRef = useRef(null);
  const eventHandlers = useMemo(() => ({
    dragend: () => {
      const m = markerRef.current;
      if (!m) return;
      const { lat, lng } = m.getLatLng();
      onChange({ ...(value || {}), lat, lon: lng, place_id: null, address: `${lat.toFixed(6)}, ${lng.toFixed(6)} (ajustado manualmente)` });
    },
  }), [value, onChange]);

  const center = value ? [value.lat, value.lon] : [23.6345, -102.5528];

  return (
    <div ref={boxRef} className="space-y-3">
      <div className="relative">
        <div className="flex items-center gap-2">
          <MapPin size={16} className="text-blue-900 shrink-0" />
          <Input
            data-testid="address-input"
            value={value ? (value.name || value.address || `${value.lat?.toFixed(5)}, ${value.lon?.toFixed(5)}`) : query}
            onChange={(e) => { if (value) return; setQuery(e.target.value); }}
            onFocus={() => { if (!value && candidates.length) setOpen(true); }}
            placeholder="Escribe una dirección, negocio o coordenadas 19.7008,-101.1844…"
            className="flex-1 bg-white"
            disabled={!!value}
          />
          {loading && !value && <Loader2 size={14} className="animate-spin text-slate-500 absolute right-3 top-1/2 -translate-y-1/2" />}
          {value && (
            <button type="button" onClick={clearSelection} data-testid="address-clear-btn" className="text-xs text-red-700 hover:underline flex items-center gap-1">
              <X size={12} />Cambiar
            </button>
          )}
        </div>

        {open && !value && candidates.length > 0 && (
          <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 shadow-lg z-[1000] max-h-72 overflow-y-auto" data-testid="address-dropdown">
            {candidates.map((c, i) => (
              <button
                key={i}
                type="button"
                onClick={() => pick(c)}
                data-testid={`address-candidate-${i}`}
                className="w-full text-left px-3 py-2 border-b border-slate-100 last:border-b-0 hover:bg-blue-50 transition-colors"
              >
                <div className="font-semibold text-sm text-slate-900 truncate">{c.name || '(sin nombre)'}</div>
                {c.address && <div className="text-xs text-slate-500 mt-0.5 truncate">{c.address}</div>}
                <div className="text-[10px] text-slate-400 mt-0.5">{c.lat?.toFixed(5)}, {c.lon?.toFixed(5)} · {c.type_detected}</div>
              </button>
            ))}
          </div>
        )}

        {open && !value && !loading && query.trim().length >= 3 && candidates.length === 0 && (
          <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 px-3 py-2 text-xs text-slate-500 z-[1000]">
            Sin coincidencias · prueba con una dirección más específica
          </div>
        )}
      </div>

      {value && (
        <>
          <div className="border border-blue-900 bg-white p-3 flex items-start justify-between gap-3" data-testid="address-selected">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-blue-900 font-semibold">Punto seleccionado</div>
              <div className="font-semibold text-slate-900 mt-1 truncate">{value.name || '(sin nombre)'}</div>
              {value.address && <div className="text-xs text-slate-600 mt-0.5 truncate">{value.address}</div>}
              <div className="text-[10px] text-slate-500 mt-1">
                {value.lat?.toFixed(6)}, {value.lon?.toFixed(6)} · radio {radiusKm} km · Arrastra el pin para afinar el punto exacto
              </div>
            </div>
          </div>

          <div style={{ height: 320 }} className="border border-slate-200 overflow-hidden" data-testid="address-map">
            <MapContainer center={center} zoom={15} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
              <TileLayer
                attribution='&copy; OpenStreetMap contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapController lat={value.lat} lon={value.lon} />
              <Circle center={center} radius={radiusKm * 1000} pathOptions={{ color: '#1e3a8a', fillOpacity: 0.06, weight: 1.5 }} />
              <Marker
                position={center}
                draggable
                eventHandlers={eventHandlers}
                ref={markerRef}
              />
            </MapContainer>
          </div>
        </>
      )}
    </div>
  );
}
